import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DEFAULT_SETTINGS = {
  hubTitle: 'How can we help you?',
  hubSubtitle: 'Search our knowledge base, browse articles, or get in touch with our support team',
  liveChatEnabled: true,
  ticketSubmissionEnabled: true,
  knowledgeBaseEnabled: true,
  videoTutorialsEnabled: true,
  faqEnabled: true,
  supportEmail: 'support@webforms.biz',
  liveChatWelcomeMessage: 'Hi! How can we help you today?',
  ticketResponseTime: '24 hours',
  categories: [
    { id: 1, title: 'Knowledge Base', articleCount: 156, enabled: true },
    { id: 2, title: 'FAQ', articleCount: 48, enabled: true },
    { id: 3, title: 'Video Tutorials', articleCount: 32, enabled: true },
    { id: 4, title: 'Form Building', articleCount: 42, enabled: true },
    { id: 5, title: 'Account Management', articleCount: 28, enabled: true },
    { id: 6, title: 'Integrations', articleCount: 64, enabled: true },
    { id: 7, title: 'Billing', articleCount: 22, enabled: true },
    { id: 8, title: 'Troubleshooting', articleCount: 38, enabled: true },
  ],
  trendingTopics: ['Form Builder Basics', 'Payment Integration', 'Managing Submissions', 'Analytics Dashboard'],
};

const SupportHubPanel = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [activeSection, setActiveSection] = useState('general');

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleCategory = (id) => {
    setSettings(prev => ({
      ...prev,
      categories: prev?.categories?.map(c => c?.id === id ? { ...c, enabled: !c?.enabled } : c),
    }));
  };

  const addTrendingTopic = () => {
    if (newTopic?.trim() && !settings?.trendingTopics?.includes(newTopic?.trim())) {
      setSettings(prev => ({ ...prev, trendingTopics: [...prev?.trendingTopics, newTopic?.trim()] }));
      setNewTopic('');
    }
  };

  const removeTrendingTopic = (topic) => {
    setSettings(prev => ({ ...prev, trendingTopics: prev?.trendingTopics?.filter(t => t !== topic) }));
  };

  const sections = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'features', label: 'Features', icon: 'ToggleLeft' },
    { id: 'categories', label: 'Categories', icon: 'Grid3x3' },
    { id: 'topics', label: 'Trending Topics', icon: 'TrendingUp' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Headphones" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">Support Hub Settings</h2>
            <p className="text-sm text-muted-foreground">Configure the Help & Support center for all users</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="RotateCcw" iconPosition="left" onClick={handleReset}>
            Reset
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName={saved ? 'Check' : 'Save'}
            iconPosition="left"
            onClick={handleSave}
            className={saved ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
      </div>
      {/* Section Tabs */}
      <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1 overflow-x-auto">
        {sections?.map(s => (
          <button
            key={s?.id}
            onClick={() => setActiveSection(s?.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth whitespace-nowrap ${
              activeSection === s?.id
                ? 'bg-card text-foreground shadow-sm border border-border'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name={s?.icon} size={14} />
            {s?.label}
          </button>
        ))}
      </div>
      {/* General Settings */}
      {activeSection === 'general' && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-5">
          <h3 className="text-base font-heading font-semibold text-foreground">Hero Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Hub Title</label>
              <input
                type="text"
                value={settings?.hubTitle}
                onChange={e => updateField('hubTitle', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Hub Subtitle</label>
              <textarea
                value={settings?.hubSubtitle}
                onChange={e => updateField('hubSubtitle', e?.target?.value)}
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth resize-none"
              />
            </div>
          </div>

          <div className="border-t border-border pt-5">
            <h3 className="text-base font-heading font-semibold text-foreground mb-4">Contact Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Support Email</label>
                <input
                  type="email"
                  value={settings?.supportEmail}
                  onChange={e => updateField('supportEmail', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Ticket Response Time</label>
                <select
                  value={settings?.ticketResponseTime}
                  onChange={e => updateField('ticketResponseTime', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                >
                  <option>1 hour</option>
                  <option>4 hours</option>
                  <option>12 hours</option>
                  <option>24 hours</option>
                  <option>48 hours</option>
                  <option>3-5 business days</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-5">
            <h3 className="text-base font-heading font-semibold text-foreground mb-4">Live Chat</h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Welcome Message</label>
              <input
                type="text"
                value={settings?.liveChatWelcomeMessage}
                onChange={e => updateField('liveChatWelcomeMessage', e?.target?.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
              />
            </div>
          </div>
        </div>
      )}
      {/* Features Toggle */}
      {activeSection === 'features' && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-base font-heading font-semibold text-foreground mb-5">Enable / Disable Features</h3>
          <div className="space-y-4">
            {[
              { key: 'knowledgeBaseEnabled', label: 'Knowledge Base', desc: 'Comprehensive guides and documentation', icon: 'BookOpen' },
              { key: 'faqEnabled', label: 'FAQ Section', desc: 'Frequently asked questions', icon: 'HelpCircle' },
              { key: 'videoTutorialsEnabled', label: 'Video Tutorials', desc: 'Step-by-step video guides', icon: 'Video' },
              { key: 'liveChatEnabled', label: 'Live Chat', desc: 'Real-time chat support widget', icon: 'MessageCircle' },
              { key: 'ticketSubmissionEnabled', label: 'Ticket Submission', desc: 'Allow users to submit support tickets', icon: 'Mail' },
            ]?.map(feature => (
              <div key={feature?.key} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-md">
                    <Icon name={feature?.icon} size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{feature?.label}</p>
                    <p className="text-xs text-muted-foreground">{feature?.desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => updateField(feature?.key, !settings?.[feature?.key])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings?.[feature?.key] ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      settings?.[feature?.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Categories */}
      {activeSection === 'categories' && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-base font-heading font-semibold text-foreground mb-5">Support Categories</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {settings?.categories?.map(cat => (
              <div key={cat?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                <div>
                  <p className="text-sm font-medium text-foreground">{cat?.title}</p>
                  <p className="text-xs text-muted-foreground">{cat?.articleCount} articles</p>
                </div>
                <button
                  onClick={() => toggleCategory(cat?.id)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    cat?.enabled ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      cat?.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Trending Topics */}
      {activeSection === 'topics' && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-base font-heading font-semibold text-foreground mb-5">Trending Topics</h3>
          <p className="text-sm text-muted-foreground mb-4">These topics appear as quick suggestions in the search bar on the Support Hub.</p>

          <div className="flex gap-2 mb-5">
            <input
              type="text"
              value={newTopic}
              onChange={e => setNewTopic(e?.target?.value)}
              onKeyDown={e => e?.key === 'Enter' && addTrendingTopic()}
              placeholder="Add a trending topic..."
              className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
            />
            <Button variant="default" size="sm" iconName="Plus" iconPosition="left" onClick={addTrendingTopic}>
              Add
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {settings?.trendingTopics?.map(topic => (
              <div key={topic} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                <span>{topic}</span>
                <button
                  onClick={() => removeTrendingTopic(topic)}
                  className="hover:text-destructive transition-smooth ml-1"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            ))}
            {settings?.trendingTopics?.length === 0 && (
              <p className="text-sm text-muted-foreground">No trending topics yet. Add some above.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportHubPanel;
